import { useCallback, useEffect, useState } from "react";
import { Search, Download, ChevronLeft, ChevronRight, Loader2, Inbox, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { ADMIN_KINDS, StatusBadge, fmtDate, downloadBlob } from "./adminConfig";
import { RecordDrawer } from "./RecordDrawer";

const selectCls = "h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-[#111827] focus:border-[#2563EB] focus:outline-none focus:ring-1 focus:ring-[#2563EB]";
const SORTS = [["-created_at", "Newest first"], ["created_at", "Oldest first"], ["-updated_at", "Recently updated"], ["status", "Status A–Z"], ["full_name", "Name A–Z"]];

const useRecords = (kind, params) => {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await api.get(`/admin/records/${kind}`, { params });
      setState({ loading: false, error: "", data });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e), data: null });
    }
  }, [kind, JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
};

const Filters = ({ cfg, f, set, onExport, exporting, canManage }) => (
  <div className="flex flex-wrap items-center gap-3" data-testid="records-filters">
    <div className="relative min-w-[240px] flex-1">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <label htmlFor="records-search" className="sr-only">Search</label>
      <input id="records-search" value={f.qInput} onChange={(e) => set({ qInput: e.target.value })} placeholder="Search name, email, reference…" className={`${selectCls} w-full pl-9`} data-testid="records-search-input" />
    </div>
    <select aria-label="Status" value={f.status} onChange={(e) => set({ status: e.target.value, page: 1 })} className={selectCls} data-testid="records-status-filter">
      <option value="">All statuses</option>
      {cfg.statuses.map((s) => <option key={s}>{s}</option>)}
    </select>
    <select aria-label={cfg.categoryLabel} value={f.category} onChange={(e) => set({ category: e.target.value, page: 1 })} className={selectCls} data-testid="records-category-filter">
      <option value="">All {cfg.categoryLabel.toLowerCase()}s</option>
      {cfg.categories.map((s) => <option key={s}>{s}</option>)}
    </select>
    <select aria-label="Sort" value={f.sort} onChange={(e) => set({ sort: e.target.value })} className={selectCls} data-testid="records-sort-select">
      {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    <label className="flex items-center gap-2 text-sm text-slate-600">
      <input type="checkbox" checked={f.archived} onChange={(e) => set({ archived: e.target.checked, page: 1 })} className="h-4 w-4 accent-[#2563EB]" data-testid="records-archived-toggle" /> Archived
    </label>
    {canManage && (
      <button type="button" onClick={onExport} disabled={exporting} className="btn-secondary h-10 px-4 text-sm" data-testid="records-export-button">
        {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
      </button>
    )}
  </div>
);

export const RecordsView = ({ kind, onChanged, canManage }) => {
  const cfg = ADMIN_KINDS[kind];
  const [f, setF] = useState({ qInput: "", q: "", status: "", category: "", archived: false, sort: "-created_at", page: 1 });
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  useEffect(() => {
    const t = setTimeout(() => setF((p) => (p.q === p.qInput ? p : { ...p, q: p.qInput, page: 1 })), 300);
    return () => clearTimeout(t);
  }, [f.qInput]);

  const params = { q: f.q || undefined, status: f.status || undefined, category: f.category || undefined, archived: f.archived, sort: f.sort, page: f.page, page_size: 20 };
  const { loading, error, data, reload } = useRecords(kind, params);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { q, status, category, archived } = params;
      const res = await api.get(`/admin/records/${kind}/export`, { params: { q, status, category, archived }, responseType: "blob" });
      downloadBlob(res.data, `trustedleap-${kind}.csv`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid={`records-view-${kind}`}>
      <Filters cfg={cfg} f={f} set={set} onExport={exportCsv} exporting={exporting} canManage={canManage} />
      <div className="mt-5 overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead className="border-b border-slate-200 bg-[#F4F6F8] text-[11px] uppercase tracking-[0.12em] text-slate-500">
            <tr>
              <th className="px-4 py-3 font-semibold">Reference</th>
              <th className="px-4 py-3 font-semibold">Name</th>
              {cfg.columns.map(([, l]) => <th key={l} className="px-4 py-3 font-semibold">{l}</th>)}
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Received</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
            {!loading && error && <tr><td colSpan={7} className="px-4 py-16 text-center text-red-600" data-testid="records-error">{error}</td></tr>}
            {!loading && !error && data?.items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-500" data-testid="records-empty"><Inbox className="mx-auto mb-3 h-6 w-6 text-slate-300" />No records match these filters.</td></tr>
            )}
            {!loading && !error && data?.items.map((r) => (
              <tr key={r.id} onClick={() => setOpenId(r.id)} className="cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-blue-50/40" data-testid={`record-row-${r.reference}`}>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-[#0B1220]">
                  <button type="button" className="text-left hover:text-[#2563EB]" onClick={(e) => { e.stopPropagation(); setOpenId(r.id); }} data-testid={`record-open-${r.reference}`}>{r.reference}</button>
                </td>
                <td className="px-4 py-3"><span className="font-medium text-[#0B1220]">{r.full_name}</span><span className="block text-xs text-slate-500">{r.email}</span></td>
                {cfg.columns.map(([k]) => <td key={k} className="max-w-[220px] truncate px-4 py-3 text-slate-600">{r[k] ?? "—"}{k === "primary_category" && r.cv && <Paperclip className="ml-2 inline h-3.5 w-3.5 text-slate-400" aria-label="CV attached" />}</td>)}
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{fmtDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
        <span data-testid="records-total">{data ? `${data.total} record${data.total === 1 ? "" : "s"}` : ""}</span>
        <div className="flex items-center gap-2">
          <button type="button" disabled={f.page <= 1} onClick={() => set({ page: f.page - 1 })} className="rounded border border-slate-300 p-1.5 disabled:opacity-40" aria-label="Previous page" data-testid="records-prev-page"><ChevronLeft className="h-4 w-4" /></button>
          <span>Page {f.page} of {pages}</span>
          <button type="button" disabled={f.page >= pages} onClick={() => set({ page: f.page + 1 })} className="rounded border border-slate-300 p-1.5 disabled:opacity-40" aria-label="Next page" data-testid="records-next-page"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <RecordDrawer kind={kind} id={openId} canManage={canManage} onClose={() => setOpenId(null)} onChanged={() => { reload(); onChanged?.(); }} />
    </div>
  );
};
